// How long a facet click waits for further clicks before its section request
// fires. Every request cancels the one before it, so this only needs to absorb
// a quick multi-select — not (as Dawn's 800ms did) act as the rate limiter.
const FACETS_SUBMIT_DEBOUNCE_MS = 400;

// Section responses run 100-400KB each; keep the in-memory cache bounded so a
// long filtering session on a phone doesn't grow it without limit.
const FACETS_CACHE_LIMIT = 20;

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, FACETS_SUBMIT_DEBOUNCE_MS);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Mobile pill UX: click anywhere on swatch pills + instant selected styling
    this.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-swatch-pill]');
      if (!pill) return;

      // If they clicked directly on the swatch control, let the native label do its job
      if (event.target.closest('.swatch-input-wrapper')) return;

      const input = pill.querySelector('.swatch-input__input');
      if (!input || input.disabled) return;

      input.click();
    });

    this.addEventListener('keydown', (event) => {
      const pill = event.target.closest?.('[data-swatch-pill]');
      if (!pill) return;

      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();

      const input = pill.querySelector('.swatch-input__input');
      if (!input || input.disabled) return;

      input.click();
    });

    this.addEventListener('input', (event) => {
      const el = event.target;
      if (!(el instanceof HTMLInputElement)) return;

      // Remember the click until its (debounced) request fires, so a response
      // that lands in between can't re-render the group and undo it.
      if (el.type === 'checkbox' || el.type === 'radio') {
        FacetFiltersForm.rememberPendingInput(el);
      }

      if (!el.matches('.mobile-facets__checkbox, .swatch-input__input')) return;

      FacetFiltersForm.reflectPillState(el);
    });
  }

  // Make a drawer pill show the input's state right now, without waiting
  // for the server. The server-rendered `active` class has to be cleared
  // here too: leaving it on until the response arrived meant an unchecked
  // pill stayed black for the length of the network round trip.
  static reflectPillState(input) {
    const pill = input.closest('.mobile-facets__label');
    if (!pill) return;
    pill.classList.toggle('is-selected', input.checked);
    pill.classList.toggle('active', input.checked);
    if (pill.hasAttribute('aria-pressed'))
      pill.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.discardPendingInputs();
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  // Drop filter values from the URL that these results don't recognise (see
  // #FacetKnownValues in facets.liquid). Shopify still applies such a value,
  // but renders no chip for it: removing the visible chips then ends on "No
  // products found" with nothing left to remove. Re-render without them.
  static dropUnknownFilterValues() {
    const known = document.getElementById('FacetKnownValues');
    if (!known) return;
    let data;
    try {
      data = JSON.parse(known.textContent);
    } catch (error) {
      return;
    }
    const filterParams = new Set(data.params);
    const activeValues = new Set(data.active.map((pair) => pair.toLowerCase()));

    const params = new URLSearchParams(window.location.search);
    const kept = new URLSearchParams();
    let dropped = false;
    params.forEach((value, key) => {
      if (
        filterParams.has(key) &&
        !activeValues.has(`${key}=${value}`.toLowerCase())
      ) {
        dropped = true;
        return;
      }
      kept.append(key, value);
    });
    if (!dropped) return;

    const searchParams = kept.toString();
    history.replaceState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
    FacetFiltersForm.searchParamsInitial = searchParams;
    FacetFiltersForm.renderPage(searchParams, null, false);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  // ---- Pending-input bookkeeping ------------------------------------------
  // A click made while an earlier request is still in flight is re-applied on
  // top of that request's response. The click's own submit (still waiting in
  // the debounce) then sends the combined state, and its response is the one
  // that finally matches what the user sees.

  static pendingInputKey(input) {
    return `${input.name}\u0000${input.value}`;
  }

  static rememberPendingInput(input) {
    FacetFiltersForm.pendingInputs.set(
      FacetFiltersForm.pendingInputKey(input),
      {
        name: input.name,
        value: input.value,
        checked: input.checked,
      },
    );
  }

  // A chip, "Clear" link or Back replaces the whole filter state, so clicks
  // still waiting in the debounce are void: without this the debounced submit
  // fired ~400ms later, re-sent the still-ticked input and undid the Clear.
  static discardPendingInputs() {
    FacetFiltersForm.pendingInputs.clear();
    FacetFiltersForm.inputsDiscardedAt = performance.now();
  }

  static reapplyPendingInputs() {
    if (!FacetFiltersForm.pendingInputs.size) return;

    FacetFiltersForm.pendingInputs.forEach(({ name, value, checked }) => {
      const selector = `input[name="${CSS.escape(name)}"]`;
      document.querySelectorAll(selector).forEach((input) => {
        if (input.value !== value) return;
        if (input.type !== 'checkbox' && input.type !== 'radio') return;
        if (input.checked === checked) return;

        input.checked = checked;
        FacetFiltersForm.reflectPillState(input);
      });
    });
  }

  // ---- Loading state ------------------------------------------------------

  static setLoading(isLoading) {
    document
      .querySelectorAll(
        '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
      )
      .forEach((spinner) => spinner.classList.toggle('hidden', !isLoading));

    document
      .getElementById('ProductGridContainer')
      ?.querySelector('.collection')
      ?.classList.toggle('loading', isLoading);

    ['ProductCount', 'ProductCountDesktop'].forEach((id) => {
      document.getElementById(id)?.classList.toggle('loading', isLoading);
    });
  }

  // ---- Request pipeline ---------------------------------------------------

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    // Any request still in flight is for a state the user has moved past.
    // Abort it — its response must never render on top of a newer one.
    FacetFiltersForm.latestRequestId += 1;
    const requestId = FacetFiltersForm.latestRequestId;
    FacetFiltersForm.abortController?.abort();
    FacetFiltersForm.abortController = null;

    FacetFiltersForm.setLoading(true);

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const cached = FacetFiltersForm.filterData.find(
        (entry) => entry.url === url,
      );

      cached
        ? FacetFiltersForm.renderSectionFromCache(cached.html, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId) {
    const controller = new AbortController();
    FacetFiltersForm.abortController = controller;

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `Facets: section request failed with HTTP ${response.status}`,
          );
        }
        return response.text();
      })
      .then((html) => {
        // Superseded by a newer click while this was in flight.
        if (requestId !== FacetFiltersForm.latestRequestId) return;

        const parsed = FacetFiltersForm.parseSection(html);
        FacetFiltersForm.cacheSection(url, html);
        FacetFiltersForm.renderSection(parsed, event);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        if (requestId !== FacetFiltersForm.latestRequestId) return;
        FacetFiltersForm.recoverFromFailedRender(error);
      });
  }

  static renderSectionFromCache(html, event) {
    try {
      FacetFiltersForm.renderSection(
        FacetFiltersForm.parseSection(html),
        event,
      );
    } catch (error) {
      FacetFiltersForm.recoverFromFailedRender(error);
    }
  }

  // Parse once and make sure this really is the grid section. Anything else
  // (an error page, a redirect target, a truncated body) used to be rendered
  // as-is: the "remove facets no longer returned" step then wiped every
  // filter group and the missing count threw, leaving the grid stuck under
  // its white loading overlay.
  static parseSection(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    if (
      !parsed.getElementById('ProductGridContainer') ||
      !parsed.getElementById('ProductCount')
    ) {
      throw new Error('Facets: section response did not contain the grid');
    }
    return parsed;
  }

  static cacheSection(url, html) {
    FacetFiltersForm.filterData = [
      ...FacetFiltersForm.filterData.filter((entry) => entry.url !== url),
      { html, url },
    ].slice(-FACETS_CACHE_LIMIT);
  }

  static renderSection(parsed, event) {
    FacetFiltersForm.renderFilters(parsed, event);
    FacetFiltersForm.renderProductGridContainer(parsed);
    FacetFiltersForm.renderProductCount(parsed);
    FacetFiltersForm.setLoading(false);
    if (typeof initializeScrollAnimationTrigger === 'function')
      initializeScrollAnimationTrigger();
  }

  // The URL already carries the requested state (pushed when the request was
  // made, or set by the browser on back/forward), so a full load renders it
  // server-side. That beats the alternative of leaving the grid dimmed under
  // the loading overlay with filters that silently didn't apply.
  static recoverFromFailedRender(error) {
    console.error(error);
    FacetFiltersForm.setLoading(false);
    FacetFiltersForm.toggleActiveFacets(false);
    window.location.reload();
  }

  // ---- DOM rendering ------------------------------------------------------

  static renderProductGridContainer(parsed) {
    const newContainer = parsed.getElementById('ProductGridContainer');
    const container = document.getElementById('ProductGridContainer');
    if (!container || !newContainer) return;

    // Update the markup
    container.innerHTML = newContainer.innerHTML;

    // Keep data-plp-total in sync for collection-load-more.js (filtered total)
    if (newContainer.dataset && 'plpTotal' in newContainer.dataset) {
      container.dataset.plpTotal = newContainer.dataset.plpTotal || '';
      if (!container.dataset.plpTotal) {
        delete container.dataset.plpTotal;
      }
    }

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(parsed) {
    const count = parsed.getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) container.innerHTML = count;
    if (containerDesktop) containerDesktop.innerHTML = count;
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (
        !Array.from(facetDetailsElementsFromFetch).some(
          ({ id }) => currentElement.id === id,
        )
      ) {
        currentElement.remove();
      }
    });

    // The group the user clicked in. Its input may already be detached (an
    // earlier response re-rendered the group), in which case closest() finds
    // nothing and the group is simply updated like the others.
    const eventJsFilter = event?.target?.closest?.('.js-filter') || null;
    const focusedBefore = document.activeElement;

    Array.from(facetDetailsElementsFromFetch).forEach(
      (elementToRender, index, all) => {
        const currentElement = document.getElementById(elementToRender.id);

        if (currentElement) {
          FacetFiltersForm.updateFacetGroup(
            currentElement,
            elementToRender,
            currentElement === eventJsFilter,
          );
          return;
        }

        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } =
            all[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId)?.after(elementToRender);
            FacetFiltersForm.regroupSizeFacet(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement?.id) {
          document
            .querySelector(`#${elementToRender.parentElement.id} .js-filter`)
            ?.before(elementToRender);
          FacetFiltersForm.regroupSizeFacet(elementToRender);
        }
      },
    );

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Dawn moved focus to the clicked group's heading after every render
    // because the re-render had destroyed the focused input. Now that drawer
    // pills are patched in place the input survives, so only restore focus
    // when it was actually lost — moving it otherwise makes iOS scroll and
    // repaint the heading on every tap.
    if (
      eventJsFilter?.isConnected &&
      focusedBefore &&
      !focusedBefore.isConnected &&
      event.target.getAttribute('type') !== 'text'
    ) {
      const summarySelector = eventJsFilter.classList.contains(
        'mobile-facets__details',
      )
        ? '.mobile-facets__summary'
        : '.facets__summary';
      eventJsFilter.querySelector(summarySelector)?.focus();
    }

    // Clicks made while this request was in flight win over its response.
    FacetFiltersForm.reapplyPendingInputs();
  }

  // Bring one facet group in the DOM up to date with the server's version.
  // Drawer pill lists are patched item by item (see patchMobileFacetList);
  // everything else falls back to Dawn's innerHTML swap.
  static updateFacetGroup(currentElement, fetchedElement, isEventGroup) {
    const isDrawerGroup = currentElement.classList.contains(
      'mobile-facets__details',
    );

    if (isDrawerGroup) {
      if (FacetFiltersForm.patchMobileFacetList(currentElement, fetchedElement))
        return;
      currentElement.innerHTML = fetchedElement.innerHTML;
      FacetFiltersForm.regroupSizeFacet(currentElement);
      return;
    }

    if (isEventGroup) {
      // Desktop layouts: keep the open/"show more" state of the clicked group
      FacetFiltersForm.renderCounts(fetchedElement, currentElement);
      return;
    }

    currentElement.innerHTML = fetchedElement.innerHTML;
    FacetFiltersForm.regroupSizeFacet(currentElement);
  }

  // Update a drawer pill list without rebuilding it. Replacing the list's
  // innerHTML (what Dawn does) recreates every pill on every click: the
  // tapped pill loses focus, the size list paints ungrouped for a frame
  // before the grouping pass moves it back, and on iOS the whole drawer
  // visibly flashes. Patching keeps the existing nodes: only their checked /
  // disabled / active state changes, and only pills that appeared or
  // disappeared server-side are inserted or removed.
  // Returns false when the group isn't a pill list so the caller can fall
  // back to the innerHTML swap.
  static patchMobileFacetList(currentDetails, fetchedDetails) {
    const currentList = currentDetails.querySelector('.mobile-facets__list');
    const fetchedList = fetchedDetails.querySelector('.mobile-facets__list');
    if (!currentList || !fetchedList) return false;

    const keyOf = (li) => {
      const input = li.querySelector('input[name]');
      return input ? `${input.name}\u0000${input.value}` : null;
    };

    const unmatched = new Map();
    Array.from(currentList.children).forEach((li) => {
      const key = keyOf(li);
      if (key) unmatched.set(key, li);
    });

    // Size lists are regrouped below, so their DOM order is not server order
    // and must not be "corrected" item by item.
    const keepOrder = !!currentList.querySelector('.santo-size-group-title');
    let setChanged = false;
    let previous = null;

    Array.from(fetchedList.children).forEach((fetchedLi) => {
      const key = keyOf(fetchedLi);
      if (!key) return;

      let li = unmatched.get(key);
      if (li) {
        unmatched.delete(key);
        FacetFiltersForm.patchFacetItem(li, fetchedLi);
        const inPlace = previous
          ? previous.nextElementSibling === li
          : currentList.firstElementChild === li;
        if (!keepOrder && !inPlace) {
          previous ? previous.after(li) : currentList.prepend(li);
        }
      } else {
        // Adopt the server's node for a value that just became available
        li = fetchedLi;
        setChanged = true;
        previous ? previous.after(li) : currentList.prepend(li);
      }
      previous = li;
    });

    // A value the server left out (zero results for the state it rendered)
    // is dropped — unless the user has it checked. That click may be newer
    // than this response; the request it triggers renders the value active.
    unmatched.forEach((li, key) => {
      const input = li.querySelector('input[name]');
      if (input?.checked || FacetFiltersForm.pendingInputs.has(key)) return;
      li.remove();
      setChanged = true;
    });

    if (setChanged) FacetFiltersForm.regroupSizeFacet(currentDetails);
    return true;
  }

  static patchFacetItem(li, fetchedLi) {
    const input = li.querySelector('input[name]');
    const fetchedInput = fetchedLi.querySelector('input[name]');
    if (!input || !fetchedInput) return;

    if (input.checked !== fetchedInput.checked)
      input.checked = fetchedInput.checked;
    if (input.disabled !== fetchedInput.disabled)
      input.disabled = fetchedInput.disabled;

    const label = li.querySelector('.mobile-facets__label');
    const fetchedLabel = fetchedLi.querySelector('.mobile-facets__label');
    if (label && fetchedLabel) {
      label.classList.toggle(
        'active',
        fetchedLabel.classList.contains('active'),
      );
      label.classList.toggle(
        'disabled',
        fetchedLabel.classList.contains('disabled'),
      );
      label.classList.toggle('is-selected', input.checked);
      if (fetchedLabel.hasAttribute('aria-pressed')) {
        label.setAttribute(
          'aria-pressed',
          fetchedLabel.getAttribute('aria-pressed'),
        );
      }
    }

    // Screen-reader text carries the product count, which changes per filter
    // state. The visible label is left alone: it never changes for a value,
    // and the size grouping may have renamed it (OS -> One Size).
    const srText = li.querySelector('.visually-hidden');
    const fetchedSrText = fetchedLi.querySelector('.visually-hidden');
    if (
      srText &&
      fetchedSrText &&
      srText.textContent !== fetchedSrText.textContent
    ) {
      srText.textContent = fetchedSrText.textContent;
    }
  }

  // Re-run the size grouping synchronously on one group, so grouped and
  // ungrouped states never get painted in different frames.
  static regroupSizeFacet(root) {
    if (!root?.querySelector?.('input[name="filter.v.option.size"]')) return;
    if (typeof window.SantoFacets?.groupSizeFacetValues === 'function') {
      window.SantoFacets.groupSizeFacetValues(root);
    }
  }

  // Replace an element's innerHTML only when the server's version differs.
  // Identical markup re-set on every click still recreates the nodes, which
  // is a visible repaint on iOS.
  static replaceIfChanged(element, fetchedElement) {
    if (!element || !fetchedElement) return;
    if (element.innerHTML !== fetchedElement.innerHTML)
      element.innerHTML = fetchedElement.innerHTML;
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      '.active-facets-mobile',
      '.active-facets-desktop',
    ];

    activeFacetElementSelectors.forEach((selector) => {
      FacetFiltersForm.replaceIfChanged(
        document.querySelector(selector),
        html.querySelector(selector),
      );
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      '.mobile-facets__open',
      '.mobile-facets__count',
      '.sorting',
    ];

    mobileElementSelectors.forEach((selector) => {
      FacetFiltersForm.replaceIfChanged(
        document.querySelector(selector),
        html.querySelector(selector),
      );
    });

    // The toolbar Sort sheet (#FacetSortDrawerForm) sits outside the regions
    // re-rendered here. Mirror the server's sort onto its radios: otherwise,
    // after "Clear all" or Back, it keeps showing the previous sort and
    // createSearchParams re-sends it with the next filter tap.
    const renderedSort = html.querySelector(
      '#FacetSortDrawerForm input[name="sort_by"]:checked',
    )?.value;
    if (renderedSort) {
      document
        .querySelectorAll('#FacetSortDrawerForm input[name="sort_by"]')
        .forEach((input) => {
          input.checked = input.value === renderedSort;
        });
    }

    // Update "Clear selections (n)" in the mobile footer after each AJAX render.
    // Important: do NOT replace the <a> element or you'll lose FacetRemove listeners.
    const clearLink = document.querySelector('.mobile-facets__clear-all');
    const clearLinkNew = html.querySelector('.mobile-facets__clear-all');

    if (clearLink && clearLinkNew) {
      if (clearLink.textContent !== clearLinkNew.textContent)
        clearLink.textContent = clearLinkNew.textContent;
      if (clearLink.getAttribute('href') !== clearLinkNew.getAttribute('href'))
        clearLink.setAttribute('href', clearLinkNew.getAttribute('href'));
    }

    // Re-bind in case a render introduced new summaries/buttons (bindEvents
    // skips elements it has already bound).
    document
      .getElementById('FacetFiltersFormMobile')
      ?.closest('menu-drawer')
      ?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector('show-more-button .label-show-more.hidden'),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace('hidden', 'show-more-item'),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }

    FacetFiltersForm.regroupSizeFacet(target);
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  // The forms that together hold one collection state for the submitting form's
  // layout. The drawer layout splits filters (FacetFiltersFormMobile) and the
  // toolbar Sort (FacetSortDrawerForm) across two forms; the vertical/horizontal
  // layouts split sort (FacetSortForm) from filters (FacetFiltersForm).
  static companionFormIds(form) {
    if (form.id === 'FacetFiltersFormMobile') return ['FacetSortDrawerForm'];
    if (form.id === 'FacetSortDrawerForm') return ['FacetFiltersFormMobile'];
    return ['FacetSortForm', 'FacetFiltersForm', 'FacetSortDrawerForm'];
  }

  // Serialize the submitting form, then fill in any param KEY it doesn't carry
  // from its companion forms. Serializing only the submitting form used to drop
  // the other form's state: picking a Sort wiped the active filters, and ticking
  // a filter wiped the sort. The submitting form wins on conflicts (e.g. a
  // template whose filter drawer has its own sort select).
  createSearchParams(form) {
    const params = new URLSearchParams(new FormData(form));
    const ownKeys = new Set(params.keys());

    FacetFiltersForm.companionFormIds(form).forEach((id) => {
      const companion = document.getElementById(id);
      if (!companion || companion === form) return;
      new URLSearchParams(new FormData(companion)).forEach((value, key) => {
        if (!ownKeys.has(key)) params.append(key, value);
      });
    });

    return params.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    // Every click this submit carries came before a chip/Clear/Back that
    // replaced the state (event.timeStamp shares performance.now()'s clock).
    if (event.timeStamp < FacetFiltersForm.inputsDiscardedAt) return;
    // Not event.target.closest('form'): by the time the debounce fires, the
    // clicked input may have been re-rendered out of the DOM by an earlier
    // response, and a detached input has no form. This element's own form is
    // the one the listener is bound to, so it is always the right one.
    const form = this.querySelector('form');
    if (!form) return;
    // This submit carries every click made so far; nothing is pending anymore.
    FacetFiltersForm.pendingInputs.clear();
    this.onSubmitForm(this.createSearchParams(form), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.discardPendingInputs();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf('?') + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.pendingInputs = new Map();
FacetFiltersForm.inputsDiscardedAt = 0;
FacetFiltersForm.latestRequestId = 0;
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.dropUnknownFilterValues();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest('facet-filters-form') ||
      document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

window.SantoFacets = window.SantoFacets || {};

window.SantoFacets.scheduleSizeFacetGrouping =
  function scheduleSizeFacetGrouping() {
    if (window.SantoFacets._sizeGroupingScheduled) return;
    window.SantoFacets._sizeGroupingScheduled = true;

    requestAnimationFrame(() => {
      window.SantoFacets._sizeGroupingScheduled = false;

      if (typeof window.SantoFacets.groupSizeFacetValues === 'function') {
        window.SantoFacets.groupSizeFacetValues();
      }
    });
  };

window.SantoFacets.groupSizeFacetValues = function groupSizeFacetValues(
  root = document,
) {
  const APPAREL_ORDER = [
    'XXXS',
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'XXXL',
    'XXXXL',
  ];
  const APPAREL_SET = new Set(APPAREL_ORDER);

  // OS / O/S / One Size variants
  const ONE_SIZE_SET = new Set([
    'OS',
    'O/S',
    'ONE SIZE',
    'ONE-SIZE',
    'ONESIZE',
  ]);

  const getLabel = (li) => {
    const el =
      li.querySelector('.facet-checkbox__text-label') ||
      li.querySelector('.swatch-input__label') ||
      li.querySelector('label') ||
      li;
    return (el.textContent || '').trim();
  };

  const renameOneSize = (li) => {
    const labelEl = li.querySelector('.facet-checkbox__text-label');
    if (labelEl) labelEl.textContent = 'One Size';

    // Update SR text like: "OS (13 products)" -> "One Size (13 products)"
    const sr = li.querySelector('.visually-hidden');
    if (sr && sr.textContent) {
      sr.textContent = sr.textContent.replace(
        /^\s*(OS|O\/S|ONE SIZE|ONE-SIZE|ONESIZE)\b/i,
        'One Size',
      );
    }
  };

  const classify = (labelRaw) => {
    const label = labelRaw.trim();
    const upper = label.toUpperCase();

    // Accessories / one size
    if (ONE_SIZE_SET.has(upper)) return 'accessories';

    // Apparel letter sizes
    if (APPAREL_SET.has(upper)) return 'apparel';

    // Waist x Inseam like 32x32 (separate group)
    if (/^\d+\s*[xX]\s*\d+$/.test(label)) return 'waist_inseam';

    // Pure numeric (shoes vs waist by range)
    if (/^\d+(\.\d+)?$/.test(label)) {
      const n = parseFloat(label);
      if (!Number.isFinite(n)) return 'other';
      return n < 20 ? 'footwear' : 'waist';
    }

    return 'other';
  };

  const sortApparel = (a, b) => {
    const aKey = getLabel(a).trim().toUpperCase();
    const bKey = getLabel(b).trim().toUpperCase();
    return APPAREL_ORDER.indexOf(aKey) - APPAREL_ORDER.indexOf(bKey);
  };

  const sortWaistOnly = (a, b) => {
    const aNum = parseFloat(getLabel(a));
    const bNum = parseFloat(getLabel(b));
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
    return getLabel(a).localeCompare(getLabel(b), undefined, { numeric: true });
  };

  const sortWaistInseam = (a, b) => {
    const parse = (s) => {
      const m = s.match(/^(\d+)\s*[xX]\s*(\d+)$/);
      if (!m) return null;
      return { waist: parseInt(m[1], 10), inseam: parseInt(m[2], 10) };
    };

    const aLabel = getLabel(a).trim();
    const bLabel = getLabel(b).trim();
    const pa = parse(aLabel);
    const pb = parse(bLabel);

    if (pa && pb) {
      if (pa.waist !== pb.waist) return pa.waist - pb.waist;
      return pa.inseam - pb.inseam;
    }

    return aLabel.localeCompare(bLabel, undefined, { numeric: true });
  };

  const sortNumeric = (a, b) => {
    const aNum = parseFloat(getLabel(a));
    const bNum = parseFloat(getLabel(b));
    if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
    return getLabel(a).localeCompare(getLabel(b), undefined, { numeric: true });
  };

  // Find all ULs that contain the Size filter inputs
  const inputs = root.querySelectorAll('input[name="filter.v.option.size"]');
  const uls = new Set();
  inputs.forEach((input) => {
    const ul = input.closest('ul');
    if (ul) uls.add(ul);
  });

  uls.forEach((ul) => {
    // Remove old headings if this reruns
    ul.querySelectorAll('li.santo-size-group-title').forEach((n) => n.remove());

    // Collect LI items that represent size values
    const items = Array.from(ul.querySelectorAll('li')).filter((li) =>
      li.querySelector('input[name="filter.v.option.size"]'),
    );
    if (!items.length) return;

    // Remove Dawn show-more hiding (grouping makes it pointless)
    items.forEach((li) => li.classList.remove('show-more-item', 'hidden'));

    const groups = {
      apparel: [],
      waist: [],
      waist_inseam: [],
      footwear: [],
      accessories: [],
      other: [],
    };

    items.forEach((li) => {
      const label = getLabel(li);
      const groupKey = classify(label);

      // If it's OS/O-S/etc, make it human-readable
      if (groupKey === 'accessories') {
        renameOneSize(li);
      }

      groups[groupKey].push(li);
    });

    groups.apparel.sort(sortApparel);
    groups.waist.sort(sortWaistOnly);
    groups.waist_inseam.sort(sortWaistInseam);
    groups.footwear.sort(sortNumeric);
    // accessories: leave as-is
    // other: leave as-is

    // Remove all existing items from UL and rebuild
    items.forEach((li) => li.remove());

    const appendGroup = (title, lis) => {
      if (!lis.length) return;
      const heading = document.createElement('li');
      heading.className = 'santo-size-group-title';
      heading.textContent = title;
      heading.setAttribute('aria-hidden', 'true');
      ul.appendChild(heading);
      lis.forEach((li) => ul.appendChild(li));
    };

    appendGroup('Apparel', groups.apparel);

    // split waist sizes vs waist×inseam
    appendGroup('Waist', groups.waist);
    appendGroup('Waist x Inseam', groups.waist_inseam);

    appendGroup('Footwear', groups.footwear);
    appendGroup('Accessories', groups.accessories);
    appendGroup('Other', groups.other);

    // Hide any show-more button inside this facet if present
    const details = ul.closest('details');
    const showMore = details?.querySelector('show-more-button');
    if (showMore) showMore.style.display = 'none';
  });
};

// Initial run on page load
document.addEventListener('DOMContentLoaded', () => {
  if (
    window.SantoFacets &&
    typeof window.SantoFacets.groupSizeFacetValues === 'function'
  ) {
    window.SantoFacets.groupSizeFacetValues();
  }
});
